import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion2';
import Title from '@/components/ui/Title';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Plus } from 'lucide-react';
import type { OfferTemplateContentInput, OfferTemplateInput } from '@/features/webinar/types';
import OfferForm, { type OfferFormValues } from './OfferForm';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import {
  createOfferTemplate,
  deleteOfferTemplate,
  deleteOfferTemplateContent,
  fetchOfferTemplate,
  listOfferTemplates,
  updateOfferTemplate,
  uploadOfferTemplateContentImage,
  upsertOfferTemplateContent,
} from '@/features/webinar/webinarThunks';
import type { RootState } from '@/app/store';
import {
  fetchOfferPurchaseNotificationConfig,
  updateOfferPurchaseNotificationConfig,
} from '@/features/live/liveThunks';
import type { WebinarPurchaseNotificationConfigInput } from '@/features/live/types';
import type { WorkspaceVisualIdentity } from '@/features/workspace/types';
// import EmptyInputFile from '../EmptyInputFile';
import { toast } from 'react-toastify';
import { mapErrorMessage } from '@/lib/mapApiError';
import { cn } from '@/lib/utils';
import type { AppError } from '@/features/auth/types';
import { getEvergreenTimelineResourceIds } from '@/features/webinar/evergreenTimelineUtils';
import { mergeOfferFormStates, type OfferFormState } from '@/lib/offerFormStates';
import { saveCreatedOffer } from '@/lib/offerCreationRollback';
import { isOfferEmbedCodeRejected, toOfferConversionInput } from '@/lib/offerEmbed';
import EmptyInputFile from '../EmptyInputFile';
import TimeCodeComponent, { type TimeCodeScheduling } from '../../evergreen/TimeCodeComponent';

const MAIN_ACCORDION_VALUE = 'accordion-0';

// Échec de la notification d'achat : il a son propre message, distinct des erreurs de l'API.
class PurchaseNotificationSaveError extends Error {}

interface OffersFormProps {
  webinarId: number;
  workspaceId: number;
  isEdit?: boolean;
  isEvergreen?: boolean;
  scheduling?: TimeCodeScheduling;
  videoUrl?: string;
  videoFallbackUrl?: string;
  readOnly?: boolean;
  openAccordionTrigger?: boolean;
  onAccordionOpened?: () => void;
}

const getColorValueById = (
  colorId: string | undefined,
  customColor: string | undefined,
  defaultColor: string,
  visualIdentity: WorkspaceVisualIdentity | null | undefined,
): string => {
  if (!colorId || colorId === 'custom') {
    return customColor || defaultColor;
  }

  if (/^#[0-9A-Fa-f]{6}$/.test(colorId)) {
    return colorId;
  }

  if (visualIdentity) {
    if (colorId === 'accent') {
      return visualIdentity.accentColor || defaultColor;
    }
    if (colorId === 'background') {
      return visualIdentity.backgroundColor || defaultColor;
    }
    if (colorId === 'contrast') {
      return visualIdentity.contrastColor || defaultColor;
    }
    if (colorId === 'secondary-background') {
      return visualIdentity.secondaryBackgroundColor || defaultColor;
    }
    if (colorId === 'gradient1-color1') {
      return 'gradient1';
    }
    if (colorId === 'gradient2-color1') {
      return 'gradient2';
    }
  }

  if (colorId === 'black') {
    return '#000000';
  }
  if (colorId === 'white') {
    return '#FFFFFF';
  }
  if (colorId === 'no-border') {
    return 'transparent';
  }

  return defaultColor;
};

/** Jetons sémantiques « gradient1 » / « gradient2 » valables pour certains champs ; l’API exige un hex pour iconColor / iconBackgroundColor. */
const resolveOfferIconColorForApi = (
  value: string | null | undefined,
  visualIdentity: WorkspaceVisualIdentity | null | undefined,
): string | null => {
  if (value == null || value === '') {
    return null;
  }
  if (value === 'gradient1') {
    return visualIdentity?.gradient1Color1 ?? '#000000';
  }
  if (value === 'gradient2') {
    return visualIdentity?.gradient2Color1 ?? '#000000';
  }
  return value;
};

export const mapOfferContentToInput = (
  data: OfferFormValues,
  type: 'A' | 'B',
  visualIdentity: WorkspaceVisualIdentity | null | undefined,
): OfferTemplateContentInput => {
  const showIcon = type === 'A' ? data.showIconA : (data.showIconB ?? false);
  const title = (type === 'A' ? data.titleA : data.titleB || '').trim();
  const subTitle = type === 'A' ? data.subTitleA?.trim() || null : data.subTitleB?.trim() || null;
  const description =
    type === 'A' ? data.descriptionA?.trim() || null : data.descriptionB?.trim() || null;
  const buttonLabel = (type === 'A' ? data.buttonLabelA : data.buttonLabelB || '').trim();
  const conversion = toOfferConversionInput(
    type === 'A' ? data.conversionModeA : data.conversionModeB,
    type === 'A' ? data.buttonLinkA : data.buttonLinkB,
    type === 'A' ? data.embedCodeA : data.embedCodeB,
    type === 'A' ? data.stripePricesA : data.stripePricesB,
  );
  const backgroundColor = getColorValueById(
    type === 'A' ? data.backgroundColorA : data.backgroundColorB,
    type === 'A' ? data.customBackgroundColorA : data.customBackgroundColorB,
    '#000000',
    visualIdentity,
  );
  const borderColorId = type === 'A' ? data.borderColorA : data.borderColorB;
  const borderColor =
    borderColorId === 'no-border'
      ? null
      : getColorValueById(
          borderColorId,
          type === 'A' ? data.customBorderColorA : data.customBorderColorB,
          '#000000',
          visualIdentity,
        );
  const titleColor = type === 'A' ? data.titleColorA : data.titleColorB || '#FFFFFF';
  const textColor = type === 'A' ? data.textColorA : data.textColorB || '#FFFFFF';
  const buttonColor = getColorValueById(
    type === 'A' ? data.buttonColorA : data.buttonColorB,
    type === 'A' ? data.customButtonColorA : data.customButtonColorB,
    '#000000',
    visualIdentity,
  );
  const buttonTextColor = type === 'A' ? data.buttonTextColorA : data.buttonTextColorB || '#000000';
  const icon = showIcon ? (type === 'A' ? data.iconA : data.iconB || null) : null;
  const iconColorId = type === 'A' ? data.iconColorA : data.iconColorB;
  const iconColor = resolveOfferIconColorForApi(
    iconColorId
      ? getColorValueById(
          iconColorId,
          type === 'A' ? data.customIconColorA : data.customIconColorB,
          '#000000',
          visualIdentity,
        )
      : null,
    visualIdentity,
  );
  const iconBackgroundColorId =
    type === 'A' ? data.iconBackgroundColorA : data.iconBackgroundColorB;
  const iconBackgroundColor = resolveOfferIconColorForApi(
    iconBackgroundColorId
      ? getColorValueById(
          iconBackgroundColorId,
          type === 'A' ? data.customIconBackgroundColorA : data.customIconBackgroundColorB,
          '#000000',
          visualIdentity,
        )
      : null,
    visualIdentity,
  );

  return {
    title,
    subTitle,
    description,
    buttonLabel,
    ...conversion,
    backgroundColor,
    borderColor,
    titleColor,
    textColor,
    buttonColor,
    buttonTextColor,
    icon,
    iconColor,
    iconBackgroundColor,
  };
};

export default function OffersForm({
  webinarId,
  workspaceId,
  isEdit,
  isEvergreen,
  scheduling,
  videoUrl,
  videoFallbackUrl,
  readOnly,
  openAccordionTrigger,
  onAccordionOpened,
}: OffersFormProps) {
  const locked = readOnly === true;
  const { t } = useTranslation('createWebinar');
  const dispatch = useAppDispatch();
  const [offers, setOffers] = useState<OfferFormState[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [submittingOffers, setSubmittingOffers] = useState<Record<string, boolean>>({});
  const [openOfferIndex, setOpenOfferIndex] = useState<string | undefined>('');
  const [mainAccordionOpen, setMainAccordionOpen] = useState<string>('');
  const sectionRef = useRef<HTMLDivElement>(null);

  const shouldScrollToSection = useRef(false);

  useEffect(() => {
    if (!openAccordionTrigger) return;
    setMainAccordionOpen(MAIN_ACCORDION_VALUE);
    shouldScrollToSection.current = true;
    onAccordionOpened?.();
  }, [openAccordionTrigger, onAccordionOpened]);

  // Le bloc est en bas de la section Live : sans ce défilement on arrive au-dessus de lui.
  // Tant que les offres chargent, le squelette rendu plus bas ne porte pas le conteneur,
  // donc on attend la fin du chargement pour défiler.
  useEffect(() => {
    if (!shouldScrollToSection.current || isLoading) return;
    shouldScrollToSection.current = false;
    const timer = setTimeout(() => {
      sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
    return () => clearTimeout(timer);
  }, [isLoading]);

  const offerTemplates = useAppSelector((state: RootState) => state.webinar.offerTemplates);
  const offerTemplatesLoading = useAppSelector(
    (state: RootState) => state.webinar.offerTemplatesLoading,
  );
  const evergreenTimeline = useAppSelector((state: RootState) => state.webinar.evergreenTimeline);
  const evergreenOfferIds = useMemo(
    () =>
      isEvergreen && !scheduling
        ? getEvergreenTimelineResourceIds(evergreenTimeline, 'OFFER')
        : null,
    [isEvergreen, scheduling, evergreenTimeline],
  );
  const visualIdentity = useAppSelector(
    (state: RootState) => state.workspace.currentWorkspace?.visualIdentity,
  );

  useEffect(() => {
    const fetchOffers = async () => {
      try {
        setIsLoading(true);
        await dispatch(listOfferTemplates({ workspaceId, webinarId })).unwrap();
      } catch (error) {
        console.error('Error fetching offers:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchOffers();
  }, [workspaceId, webinarId, dispatch]);

  useEffect(() => {
    if (offerTemplatesLoading) return;

    let filteredOffers = offerTemplates.filter((offer) => !offer.thankYouPageOffer);
    if (evergreenOfferIds) {
      filteredOffers = filteredOffers.filter((offer) => evergreenOfferIds.has(offer.id));
    }

    if (filteredOffers.length === 0 && !evergreenOfferIds) return;

    const savingIds = new Set(
      Object.entries(submittingOffers)
        .filter(([, isSubmitting]) => isSubmitting)
        .map(([id]) => id),
    );

    setOffers((prevOffers) => mergeOfferFormStates(prevOffers, filteredOffers, { savingIds }));
  }, [offerTemplatesLoading, offerTemplates, evergreenOfferIds, submittingOffers]);

  const addOffer = () => {
    if (locked) return;
    const newOfferId = crypto.randomUUID();
    setOffers((prevOffers) => [...prevOffers, { id: newOfferId }]);
    setOpenOfferIndex(newOfferId);
  };

  const removeOffer = async (offerId: string) => {
    if (locked) return;
    const offerToRemove = offers.find((offer) => offer.id === offerId);
    const templateId = offerToRemove?.templateId || offerToRemove?.existingOffer?.id;

    setOffers((prevOffers) => prevOffers.filter((offer) => offer.id !== offerId));
    if (templateId) {
      try {
        const existingOffer = offerToRemove?.existingOffer;

        if (existingOffer?.contents) {
          const contentA = existingOffer.contents.find((c) => c.type === 'A');
          if (contentA) {
            dispatch(
              deleteOfferTemplateContent({
                workspaceId,
                webinarId,
                templateId,
                contentType: 'A',
              }),
            ).unwrap();
          }

          const contentB = existingOffer.contents.find((c) => c.type === 'B');
          if (contentB) {
            dispatch(
              deleteOfferTemplateContent({
                workspaceId,
                webinarId,
                templateId,
                contentType: 'B',
              }),
            ).unwrap();
          }
        }

        dispatch(
          deleteOfferTemplate({
            workspaceId,
            webinarId,
            offerId: templateId,
          }),
        );

        // await dispatch(listOfferTemplates({ workspaceId, webinarId })).unwrap();
      } catch (error) {
        console.error('Error deleting offer:', error);
      }
    }
  };

  const savePurchaseNotification = async (
    templateId: number,
    input: WebinarPurchaseNotificationConfigInput,
  ): Promise<boolean> => {
    const result = await dispatch(
      updateOfferPurchaseNotificationConfig({ workspaceId, webinarId, offerId: templateId, input }),
    );
    if (updateOfferPurchaseNotificationConfig.rejected.match(result)) return false;
    dispatch(fetchOfferPurchaseNotificationConfig({ workspaceId, webinarId, offerId: templateId }));
    return true;
  };

  // Un thunk rejeté résout au lieu de lever : on teste le résultat pour savoir si l'offre est partie.
  const rollbackCreatedOffer = async (createdTemplateId: number, offerId: string) => {
    const rollback = await dispatch(
      deleteOfferTemplate({ workspaceId, webinarId, offerId: createdTemplateId }),
    );
    if (deleteOfferTemplate.rejected.match(rollback)) return false;
    // L'offre n'existe plus en base : le formulaire redevient une création, avec sa saisie.
    setOffers((prevOffers) =>
      prevOffers.map((offer) =>
        offer.id === offerId
          ? { ...offer, templateId: undefined, existingOffer: undefined }
          : offer,
      ),
    );
    return true;
  };

  const handleOfferSubmit = async (
    offerData: OfferFormValues,
    offerId: string,
    purchaseNotificationInput?: WebinarPurchaseNotificationConfigInput,
  ): Promise<number | null> => {
    if (locked) return null;
    try {
      setSubmittingOffers((prev) => ({ ...prev, [offerId]: true }));
      const currentOffer = offers.find((o) => o.id === offerId);
      const templateId = currentOffer?.templateId || currentOffer?.existingOffer?.id;

      if (!templateId) {
        const offerTemplateInput: OfferTemplateInput = {
          privateTitle: offerData.privateTitle.trim(),
          abTesting: offerData.abTesting ?? false,
          splitPercentA: offerData.abTesting ? (offerData.splitPercentA ?? 50) : null,
        };

        const createdTemplateId = await dispatch(
          createOfferTemplate({
            workspaceId,
            webinarId,
            input: offerTemplateInput,
          }),
        ).unwrap();

        if (!createdTemplateId) {
          console.error('Failed to create offer template');
          return null;
        }

        // Les thunks de contenu relancent le listing : sans ce rattachement immédiat,
        // la liste rapatriée ne reconnaît pas l'offre en cours et la dédouble.
        setOffers((prevOffers) =>
          prevOffers.map((offer) =>
            offer.id === offerId ? { ...offer, templateId: createdTemplateId } : offer,
          ),
        );

        const saved = await saveCreatedOffer(
          async () => {
            const contentA: OfferTemplateContentInput = mapOfferContentToInput(
              offerData,
              'A',
              visualIdentity,
            );

            await dispatch(
              upsertOfferTemplateContent({
                workspaceId,
                webinarId,
                templateId: createdTemplateId,
                contentType: 'A',
                input: contentA,
              }),
            ).unwrap();

            if (offerData.showImageA && offerData.imgA instanceof File) {
              await dispatch(
                uploadOfferTemplateContentImage({
                  workspaceId,
                  webinarId,
                  templateId: createdTemplateId,
                  contentType: 'A',
                  file: offerData.imgA,
                }),
              ).unwrap();
            }

            if (offerData.abTesting) {
              const contentB: OfferTemplateContentInput = mapOfferContentToInput(
                offerData,
                'B',
                visualIdentity,
              );
              await dispatch(
                upsertOfferTemplateContent({
                  workspaceId,
                  webinarId,
                  templateId: createdTemplateId,
                  contentType: 'B',
                  input: contentB,
                }),
              ).unwrap();

              if (offerData.showImageB && offerData.imgB instanceof File) {
                await dispatch(
                  uploadOfferTemplateContentImage({
                    workspaceId,
                    webinarId,
                    templateId: createdTemplateId,
                    contentType: 'B',
                    file: offerData.imgB,
                  }),
                ).unwrap();
              }
            }

            if (
              purchaseNotificationInput &&
              !(await savePurchaseNotification(createdTemplateId, purchaseNotificationInput))
            ) {
              throw new PurchaseNotificationSaveError();
            }
          },
          () => rollbackCreatedOffer(createdTemplateId, offerId),
        );

        if (saved.outcome === 'leftOver') {
          console.error(
            `Offer template ${createdTemplateId} left over after a failed creation`,
            saved.error,
          );
          toast.error(t('offersForm.offerRollbackFailed'));
          return null;
        }
        if (saved.outcome === 'rolledBack') {
          if (saved.error instanceof PurchaseNotificationSaveError) {
            toast.error(t('popupForm.errors.saveError'));
            return null;
          }
          throw saved.error;
        }

        const createdOfferResult = await dispatch(
          fetchOfferTemplate({
            workspaceId,
            webinarId,
            offerId: createdTemplateId,
          }),
        );
        const createdOffer = fetchOfferTemplate.fulfilled.match(createdOfferResult)
          ? createdOfferResult.payload
          : undefined;

        setOffers((prevOffers) =>
          prevOffers.map((offer) =>
            offer.id === offerId
              ? {
                  ...offer,
                  templateId: createdTemplateId,
                  existingOffer: createdOffer ?? offer.existingOffer,
                }
              : offer,
          ),
        );

        toast.success(t('offersForm.offerCreatedSuccess'));
        return createdTemplateId;
      } else {
        const existingOffer = currentOffer?.existingOffer;

        // L'offre peut exister en base sans être encore rapatriée (création interrompue après
        // le POST) : sans référence de comparaison, on renvoie le template plutôt que d'abandonner.
        const hasTemplateFieldsChanged =
          !existingOffer ||
          existingOffer.privateTitle !== offerData.privateTitle ||
          existingOffer.abTesting !== (offerData.abTesting ?? false) ||
          existingOffer.splitPercentA !==
            (offerData.abTesting ? (offerData.splitPercentA ?? 50) : null);

        if (hasTemplateFieldsChanged) {
          const offerTemplateInput: OfferTemplateInput = {
            privateTitle: offerData.privateTitle.trim(),
            abTesting: offerData.abTesting ?? false,
            splitPercentA: offerData.abTesting ? (offerData.splitPercentA ?? 50) : null,
          };

          await dispatch(
            updateOfferTemplate({
              workspaceId,
              webinarId,
              offerId: templateId,
              input: offerTemplateInput,
            }),
          ).unwrap();
        }

        const contentA: OfferTemplateContentInput = mapOfferContentToInput(
          offerData,
          'A',
          visualIdentity,
        );
        await dispatch(
          upsertOfferTemplateContent({
            workspaceId,
            webinarId,
            templateId,
            contentType: 'A',
            input: contentA,
          }),
        ).unwrap();

        if (offerData.showImageA && offerData.imgA instanceof File) {
          await dispatch(
            uploadOfferTemplateContentImage({
              workspaceId,
              webinarId,
              templateId,
              contentType: 'A',
              file: offerData.imgA,
            }),
          ).unwrap();
        }

        if (offerData.abTesting) {
          const contentB: OfferTemplateContentInput = mapOfferContentToInput(
            offerData,
            'B',
            visualIdentity,
          );
          await dispatch(
            upsertOfferTemplateContent({
              workspaceId,
              webinarId,
              templateId,
              contentType: 'B',
              input: contentB,
            }),
          ).unwrap();

          if (offerData.showImageB && offerData.imgB instanceof File) {
            await dispatch(
              uploadOfferTemplateContentImage({
                workspaceId,
                webinarId,
                templateId,
                contentType: 'B',
                file: offerData.imgB,
              }),
            ).unwrap();
          }
        }

        if (
          purchaseNotificationInput &&
          !(await savePurchaseNotification(templateId, purchaseNotificationInput))
        ) {
          toast.error(t('popupForm.errors.saveError'));
          return null;
        }

        const updatedOffer = await dispatch(
          fetchOfferTemplate({
            workspaceId,
            webinarId,
            offerId: templateId,
          }),
        ).unwrap();

        toast.success(t('offersForm.offerUpdatedSuccess'));

        setOffers((prevOffers) =>
          prevOffers.map((offer) =>
            offer.id === offerId ? { ...offer, existingOffer: updatedOffer } : offer,
          ),
        );
        return templateId;
      }
    } catch (error) {
      console.error('Error submitting offer:', error);
      const err = error as AppError | { message?: string; code?: string; status?: number };
      if (isOfferEmbedCodeRejected(err)) {
        toast.error(t('errors.offerEmbedCodeInvalid'));
        return null;
      }
      const errorMessage = mapErrorMessage(err?.message, err?.code, err?.status);
      toast.error(t(errorMessage) || t('errors.generic'));
      return null;
    } finally {
      setSubmittingOffers((prev) => ({ ...prev, [offerId]: false }));
    }
  };

  if (isLoading) {
    return (
      <div>
        <Accordion type="single" collapsible defaultValue="item-1">
          <AccordionItem
            value="item-1"
            className={`bg-white p-0 ${isEdit ? 'accordion-page' : ''}`}
          >
            <AccordionTrigger className="rounded-[20px]">
              <div className="flex flex-col  gap-2">
                <Title level={2}>{t('offersForm.sectionTitle')}</Title>
                <p className="text-sm font-normal text-tertiary">{t('offersForm.description')}</p>
              </div>
            </AccordionTrigger>
            <AccordionContent
              className={`flex flex-col gap-4 rounded-[20px] bg-white pt-4 p-0 ${isEdit ? 'pb-4' : ''}`}
            >
              <div className="space-y-[10px]">
                <div className="space-y-4 rounded-lg p-4">
                  <Skeleton className="h-6 w-32" />
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-24 w-full" />
                  <div className="flex gap-2">
                    <Skeleton className="h-10 w-24" />
                    <Skeleton className="h-10 w-24" />
                  </div>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
        <div className="flex items-center gap-2 pt-4">
          <Skeleton className="h-6 w-6" />
          <Skeleton className="h-5 w-40" />
        </div>
      </div>
    );
  }

  const nonThankYouOffers = offerTemplates.filter((offer) => !offer.thankYouPageOffer);
  const offersForAccordionFill = evergreenOfferIds
    ? nonThankYouOffers.filter((o) => evergreenOfferIds.has(o.id))
    : nonThankYouOffers;

  return (
    <div ref={sectionRef}>
      <Accordion
        type="single"
        collapsible
        value={mainAccordionOpen}
        onValueChange={setMainAccordionOpen}
      >
        <AccordionItem
          value={MAIN_ACCORDION_VALUE}
          className={`bg-white p-0  ${isEdit ? 'accordion-page' : ''}`}
        >
          <AccordionTrigger
            fillForm={offersForAccordionFill.length > 0}
            className=" rounded-[20px]"
          >
            <div className="flex flex-col  gap-2">
              <Title level={2}>{t('offersForm.sectionTitle')}</Title>
              <p className="text-sm font-normal text-tertiary">{t('offersForm.description')}</p>
            </div>
          </AccordionTrigger>
          <AccordionContent
            className={`flex flex-col gap-4 rounded-[20px] bg-white p-0 ${isEdit ? 'pb-4 bg-subtitle' : ''}`}
          >
            {offers.length === 0 ? (
              <button
                type="button"
                onClick={() => {
                  if (!locked && (!isEvergreen || scheduling)) addOffer();
                }}
                className={cn(locked && 'cursor-default opacity-90', 'w-full text-left')}
                aria-label={t('offersForm.addOfferButton')}
              >
                <EmptyInputFile contentType={t('offersForm.emptyContentType')} type="offer" />
              </button>
            ) : (
              <>
                <Accordion
                  type="single"
                  collapsible
                  className="space-y-[10px] "
                  value={openOfferIndex}
                  onValueChange={setOpenOfferIndex}
                >
                  {offers.map((offer, index) => (
                    <div key={offer.id}>
                      <OfferForm
                        offerIndex={index}
                        accordionValue={offer.id}
                        // offerId={offer.id}
                        canRemove={offers.length > 1 || !!offer.existingOffer}
                        onRemove={() => removeOffer(offer.id)}
                        onSubmit={(data, purchaseNotificationInput) =>
                          handleOfferSubmit(data, offer.id, purchaseNotificationInput)
                        }
                        readOnly={readOnly}
                        existingOffer={
                          offer.existingOffer
                            ? ({
                                ...(offer.existingOffer.contents?.[0] || {}),
                                privateTitle: offer.existingOffer.privateTitle,
                                abTesting: offer.existingOffer.abTesting,
                                splitPercentA: offer.existingOffer.splitPercentA,
                                contents: offer.existingOffer.contents || [],
                              } as any)
                            : undefined
                        }
                        webinarId={webinarId}
                        workspaceId={workspaceId}
                        templateId={offer.templateId}
                        isSubmitting={submittingOffers[offer.id] || false}
                        isEvergreen={isEvergreen}
                      />
                      {videoUrl && (
                        <TimeCodeComponent
                          videoUrl={videoUrl}
                          scheduling={scheduling}
                          videoFallbackUrl={videoFallbackUrl}
                          selectedEventType="OFFER"
                          selectedResourceId={offer.templateId ?? offer.existingOffer?.id ?? null}
                          readOnly={locked}
                        />
                      )}
                    </div>
                  ))}
                </Accordion>

                {(!isEvergreen || !!scheduling) && !locked && (
                  <button
                    type="button"
                    onClick={addOffer}
                    className="flex items-center gap-2 pt-4"
                    aria-label={t('offersForm.addOfferButton')}
                  >
                    <Plus className="size-6" />
                    <Label className="cursor-pointer">{t('offersForm.addOfferButton')}</Label>
                  </button>
                )}
              </>
            )}
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}
